import { HardDrive, AlertTriangle, XCircle } from "lucide-react";
import { useStorageStatus } from "../hooks/useStorageStatus";
import { Progress } from "./ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface StorageStatusIndicatorProps {
  collapsed?: boolean;
}

export default function StorageStatusIndicator({
  collapsed = false,
}: StorageStatusIndicatorProps) {
  const { data: status, isLoading } = useStorageStatus();

  // Don't show if loading or no limit configured
  if (isLoading || !status || status.limit_bytes === null) {
    return null;
  }

  const percentage = Math.round(status.used_percentage);
  const isCritical = status.is_critical;
  const isWarning = status.is_warning;

  // Determine color based on status
  const getColorClasses = () => {
    if (isCritical) {
      return {
        bg: "bg-red-100",
        progress: "[&>div]:bg-red-600",
        text: "text-red-700",
        icon: "text-red-600",
      };
    }
    if (isWarning) {
      return {
        bg: "bg-amber-100",
        progress: "[&>div]:bg-amber-600",
        text: "text-amber-700",
        icon: "text-amber-600",
      };
    }
    return {
      bg: "bg-muted",
      progress: "[&>div]:bg-primary",
      text: "text-muted-foreground",
      icon: "text-muted-foreground",
    };
  };

  const colors = getColorClasses();

  const content = (
    <div className={`rounded-lg p-3 ${colors.bg}`}>
      <div className="flex items-center gap-2 mb-2">
        {isCritical ? (
          <XCircle className={`h-4 w-4 ${colors.icon}`} />
        ) : isWarning ? (
          <AlertTriangle className={`h-4 w-4 ${colors.icon}`} />
        ) : (
          <HardDrive className={`h-4 w-4 ${colors.icon}`} />
        )}
        {!collapsed && (
          <span className={`text-sm font-medium ${colors.text}`}>Storage</span>
        )}
      </div>

      <Progress value={percentage} className={`h-2 ${colors.progress}`} />

      {!collapsed && (
        <div className={`mt-2 text-xs ${colors.text}`}>
          <span className="font-medium">{status.used_bytes_human}</span>
          <span className="opacity-70"> / {status.limit_bytes_human}</span>
        </div>
      )}
    </div>
  );

  if (collapsed) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right">
            <div className="text-sm">
              <p className="font-medium">
                {status.used_bytes_human} / {status.limit_bytes_human}
              </p>
              <p className="text-muted-foreground">{percentage}% used</p>
              {isCritical && (
                <p className="text-red-600 mt-1">Uploads blocked</p>
              )}
              {isWarning && !isCritical && (
                <p className="text-amber-600 mt-1">Storage almost full</p>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
}
